import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import {
  getTabSalesReservationType,
  getTabSalesStatus,
  getTabSalesProcurementStage,
} from '@helpers';
import { selectAvailabilityPinnedFilters } from '@store';
import { PinnedFilters } from 'components/common/PinnedFilters/PinnedFilters';
import { AVAILABILITY_TABLE_TABS } from './constants';

const getFilterValue = (queryParam, data) =>
  queryParam
    ?.split(',')
    ?.map((item) =>
      data.find((option) => option.value.toString() === item.toString())
    ) || [];

const AvailabilityTableFilters = ({
  statuses = [],
  reservationsTypes = [],
  brands = [],
  servicePoints = [],
  bodyTypes = [],
  skuTypes = [],
  transmissions = [],
  fuelTypes = [],
  engineTypes = [],
  colors = [],
  procurementStages = [],
  sourcingTypes = [],
  categoryClasses = [],
  conditions = [],
}) => {
  const router = useRouter();
  const pinnedFilters = useSelector(selectAvailabilityPinnedFilters);
  const hasPinnedFilters = Boolean(pinnedFilters?.length);

  const colorValue = getFilterValue(router.query.color, colors);

  const transmissionValue = getFilterValue(
    router.query.transmission,
    transmissions
  );
  const fuelTypeValue = getFilterValue(router.query.fuelType, fuelTypes);
  const engineTypeValue = getFilterValue(router.query.engineType, engineTypes);

  const locationsFilterValue = getFilterValue(
    router.query.location,
    servicePoints
  );

  const locationOptions = useMemo(() => {
    const safe = Array.isArray(servicePoints) ? servicePoints : [];
    return safe.filter(
      (opt) =>
        opt &&
        opt.value !== null &&
        opt.value !== undefined &&
        String(opt.value).trim() !== '-' &&
        String(opt.value).trim() !== ''
    );
  }, [servicePoints]);

  const bodyTypeFilterValue = getFilterValue(router.query.bodyType, bodyTypes);
  const skuTypeFilterValue = getFilterValue(router.query.skuType, skuTypes);
  const brandFilterValue = getFilterValue(router.query.brand, brands) || [];
  const sourcingTypeFilterValue = getFilterValue(
    router.query.sourcingType,
    sourcingTypes
  );
  const categoryClassFilterValue = getFilterValue(
    router.query.categoryClass,
    categoryClasses
  );

  const conditionFilterValue = getFilterValue(
    router.query.condition,
    conditions
  );

  const models = brandFilterValue.reduce((acc, cur) => {
    acc = [...acc, ...cur.models];
    return acc;
  }, []);

  const modelFilterValue = getFilterValue(router.query.model, models).reduce(
    (acc, cur) => {
      if (cur) {
        acc.push(cur);
      }
      return acc;
    },
    []
  );

  const isSalesTab = useMemo(
    () => router.query.tab === AVAILABILITY_TABLE_TABS.sales.key,
    [router.query?.tab]
  );

  const reservationsTypesFilterValue = useMemo(() => {
    if (isSalesTab) return getTabSalesReservationType(reservationsTypes);
    return getFilterValue(router.query.reservationType, reservationsTypes);
  }, [isSalesTab, router.query.reservationType, reservationsTypes]);

  const statusesFilterValue = useMemo(() => {
    if (isSalesTab) return getTabSalesStatus(statuses);
    return getFilterValue(router.query.status, statuses);
  }, [isSalesTab, router.query.status, statuses]);

  const procurementStageValue = useMemo(() => {
    if (isSalesTab) return getTabSalesProcurementStage(procurementStages);
    return getFilterValue(router.query.procurementStageId, procurementStages);
  }, [isSalesTab, router.query.procurementStageId, procurementStages]);

  useEffect(() => {
    if (!router.query.brand && router.query.model) {
      delete router.query.model;
      router.push(router);
      // onChange(router.query);
    }

    // When models on url do not match with the selected brands
    if (router.query.brand && router.query.model) {
      const modelBrands = router.query.model.split(',');
      const matchedModels = modelBrands.reduce((acc, cur) => {
        if (
          brandFilterValue.find((brand) =>
            brand.models.find((_m) => cur === _m.value)
          )
        ) {
          acc.push(cur);
        }
        return acc;
      }, []);

      if (matchedModels.length !== modelBrands.length) {
        router.query.model = matchedModels.map((item) => item).join(',');
        router.push(router);
      }
    }
  }, [router.query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSalesTab && Object.keys(router.query || {}).length > 1) {
      const { tab } = router.query;
      router.query = { tab };
      router.push(router);
    }
  }, [isSalesTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeMultiFilter = (val, filterName) => {
    if (val.length) {
      router.query[filterName] = val.map((item) => item.value).join(',');
    } else {
      delete router.query[filterName];
    }
    router.query.page = '1';
    router.push(router);
  };

  const FILTERS_CONFIG = [
    {
      key: 'brand',
      label: 'Brand',
      type: 'filterMultiselect',
      width: '150px',
      minWidth: '150px',
      options: brands,
      value: brandFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'model',
      label: 'Model',
      type: 'filterMultiselect',
      width: '180px',
      minWidth: '300px',
      options: models,
      value: modelFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'transmission',
      label: 'Transmission',
      type: 'filterMultiselect',
      width: '180px',
      minWidth: '150px',
      options: transmissions,
      value: transmissionValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'color',
      label: 'Color',
      type: 'filterMultiselect',
      width: '150px',
      minWidth: '250px',
      options: colors,
      value: colorValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'procurementStageId',
      label: 'Stage',
      type: 'filterMultiselect',
      width: '150px',
      minWidth: '150px',
      options: procurementStages,
      value: procurementStageValue,
      hasSelectAllOption: true,
      isMulti: true,
      disabled: isSalesTab,
    },
    {
      key: 'engineType',
      label: 'Engine Type',
      type: 'filterMultiselect',
      width: '150px',
      minWidth: '180px',
      options: engineTypes,
      value: engineTypeValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'fuelType',
      label: 'Fuel Type',
      type: 'filterMultiselect',
      width: '150px',
      minWidth: '180px',
      options: fuelTypes,
      value: fuelTypeValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'skuType',
      label: 'Vehicle Type',
      type: 'filterMultiselect',
      width: '150px',
      minWidth: '180px',
      options: skuTypes,
      value: skuTypeFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'bodyType',
      label: 'Body Type',
      type: 'filterMultiselect',
      width: '150px',
      minWidth: '180px',
      options: bodyTypes,
      value: bodyTypeFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'reservationType',
      label: 'Reservation Type',
      type: 'filterMultiselect',
      width: '200px',
      minWidth: '180px',
      options: reservationsTypes,
      value: reservationsTypesFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
      disabled: isSalesTab,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'filterMultiselect',
      width: '180px',
      minWidth: '250px',
      options: statuses,
      value: statusesFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
      disabled: isSalesTab,
    },
    {
      key: 'location',
      label: 'Location',
      type: 'filterMultiselect',
      width: '200px',
      minWidth: '380px',
      options: locationOptions,
      value: locationsFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'sourcingType',
      label: 'Sourcing Type',
      type: 'filterMultiselect',
      width: '200px',
      minWidth: '180px',
      options: sourcingTypes,
      value: sourcingTypeFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'categoryClass',
      label: 'Category Class',
      type: 'filterMultiselect',
      width: '200px',
      minWidth: '180px',
      options: categoryClasses,
      value: categoryClassFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
    {
      key: 'condition',
      label: 'Condition',
      type: 'filterMultiselect',
      width: '200px',
      minWidth: '180px',
      options: conditions,
      value: conditionFilterValue,
      hasSelectAllOption: true,
      isMulti: true,
    },
  ];
  if (!hasPinnedFilters) return null;

  return (
    <PinnedFilters
      pinnedFilters={pinnedFilters}
      filtersConfig={FILTERS_CONFIG}
      handleChangeMultiFilter={handleChangeMultiFilter}
    />
  );
};

export default AvailabilityTableFilters;
